import { useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import { useSalesReportQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, formatQuantity, isoDate } from '@/lib/format';
import {
  Card, CardBody, CardHeader, EmptyState, ErrorState, Input, LoadingState, PageHeader,
  Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { ExportMenu } from '@/components/ExportMenu';

/** The ranges a shop actually asks for, so the common case is one click. */
const PRESETS: Record<string, { label: string; from: () => Date; to: () => Date }> = {
  today: { label: 'Today', from: () => new Date(), to: () => new Date() },
  week: {
    label: 'Last 7 days',
    from: () => new Date(Date.now() - 6 * 86_400_000),
    to: () => new Date(),
  },
  month: {
    label: 'Last 30 days',
    from: () => new Date(Date.now() - 29 * 86_400_000),
    to: () => new Date(),
  },
  thisMonth: {
    label: 'This month',
    from: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: () => new Date(),
  },
};

export function SalesReportPage() {
  const format = useFormat();

  const [preset, setPreset] = useState('month');
  const [from, setFrom] = useState(isoDate(PRESETS.month.from()));
  const [to, setTo] = useState(isoDate(PRESETS.month.to()));

  const { data, isLoading, isError, error, refetch, isFetching } = useSalesReportQuery({ from, to });

  const applyPreset = (key: string): void => {
    setPreset(key);
    const range = PRESETS[key];
    if (!range) return;
    setFrom(isoDate(range.from()));
    setTo(isoDate(range.to()));
  };

  const summary = data?.summary;
  const showsCost = data?.includesCost ?? false;

  // Recharts needs numbers; money crosses the wire as a decimal string.
  const series = (data?.series ?? []).map((point) => ({
    date: point.date,
    label: formatDate(point.date, format),
    Revenue: Number(point.revenue),
    ...(showsCost && point.profit !== undefined ? { Profit: Number(point.profit) } : {}),
  }));

  return (
    <>
      <PageHeader
        title="Sales report"
        description="What was sold over a period, and what it earned."
        action={
          <ExportMenu
            period={`${formatDate(from, format)} to ${formatDate(to, format)}`}
            items={[
              { label: 'Sales report — the figures and how they add up', path: '/reports/sales/export', params: { from, to } },
              { label: 'Sales listing — every sale, one per line', path: '/reports/sales/listing/export', params: { from, to } },
            ]}
          />
        }
      />

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-full sm:w-44">
            <Select label="Period" value={preset} onChange={(event) => applyPreset(event.target.value)}>
              {Object.entries(PRESETS).map(([key, range]) => (
                <option key={key} value={key}>{range.label}</option>
              ))}
              <option value="custom">Custom</option>
            </Select>
          </div>
          <div className="w-full sm:w-44">
            <Input
              label="From"
              type="date"
              value={from}
              max={to}
              onChange={(event) => {
                setFrom(event.target.value);
                setPreset('custom');
              }}
            />
          </div>
          <div className="w-full sm:w-44">
            <Input
              label="To"
              type="date"
              value={to}
              min={from}
              onChange={(event) => {
                setTo(event.target.value);
                setPreset('custom');
              }}
            />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <LoadingState label="Working out the figures…" />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : summary && data ? (
        <div className={isFetching ? 'opacity-60' : undefined}>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Revenue" value={formatMoney(summary.revenue, format)} hint={`${summary.saleCount} sale${summary.saleCount === 1 ? '' : 's'}`} />
            <Stat label="Average sale" value={formatMoney(summary.averageSale, format)} hint="Revenue ÷ sales" />
            {showsCost && summary.grossProfit !== undefined && (
              <Stat
                label="Gross profit"
                value={formatMoney(summary.grossProfit, format)}
                hint={
                  summary.marginPercent === null || summary.marginPercent === undefined
                    ? 'No revenue to measure against'
                    : `${summary.marginPercent}% margin · after cost of goods`
                }
              />
            )}
            {showsCost && summary.netProfit !== undefined && (
              <Stat
                label="Net profit"
                value={formatMoney(summary.netProfit, format)}
                hint={`after ${formatMoney(summary.expenses ?? '0', format)} of expenses`}
              />
            )}
            <Stat
              label="Still owed"
              value={formatMoney(summary.outstanding, format)}
              hint={`${formatMoney(summary.collected, format)} collected`}
            />
          </div>

          <Card className="mb-6">
            <CardHeader
              title="Over the period"
              description={
                showsCost
                  ? 'Revenue and the profit left after the cost of the goods sold.'
                  : 'Revenue per day.'
              }
            />
            <CardBody>
              {series.length === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title="Nothing sold in this period"
                  description="Pick a wider range, or check back once sales are confirmed."
                />
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="currentColor"
                        opacity={0.1}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        stroke="currentColor"
                        opacity={0.6}
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        stroke="currentColor"
                        opacity={0.6}
                        width={80}
                        tickFormatter={(value: number) => formatMoney(String(value), format)}
                      />
                      <Tooltip
                        cursor={{ stroke: 'currentColor', strokeOpacity: 0.2 }}
                        contentStyle={{
                          background: 'var(--surface-raised)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 8,
                          color: 'var(--text-primary)',
                        }}
                        formatter={(value: number, name: string) => [
                          formatMoney(String(value), format),
                          name,
                        ]}
                      />
                      {/* Two series, so identity is never colour alone: the legend
                          names them and the dash pattern tells them apart. */}
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Revenue"
                        stroke="var(--chart-1)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                      {showsCost && (
                        <Line
                          type="monotone"
                          dataKey="Profit"
                          stroke="var(--chart-2)"
                          strokeWidth={2}
                          strokeDasharray="5 3"
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardBody>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="What sold" description="By value, best first." />
              {data.topProducts.length === 0 ? (
                <EmptyState icon={BarChart3} title="Nothing sold" description="No lines in this period." />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Product</Th>
                      <Th numeric>Qty</Th>
                      <Th numeric>Revenue</Th>
                      <Can permission={P.REPORT_FINANCIAL_VIEW}>
                        <Th numeric>Profit</Th>
                      </Can>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topProducts.map((row) => (
                      <tr key={row.variantId}>
                        <Td>
                          <span className="font-medium">{row.name}</span>
                          <p className="text-xs text-muted">{row.sku}</p>
                        </Td>
                        <Td numeric className="text-secondary">
                          {formatQuantity(row.quantity, format)}
                        </Td>
                        <Td numeric className="font-medium">{formatMoney(row.revenue, format)}</Td>
                        <Can permission={P.REPORT_FINANCIAL_VIEW}>
                          <Td numeric className="text-secondary">
                            {row.profit === undefined ? '—' : formatMoney(row.profit, format)}
                          </Td>
                        </Can>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card>
              <CardHeader title="Who bought" description="By value, best first." />
              {data.topCustomers.length === 0 ? (
                <EmptyState icon={BarChart3} title="No buyers" description="No sales in this period." />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Customer</Th>
                      <Th numeric>Sales</Th>
                      <Th numeric>Revenue</Th>
                      <Th numeric>Owed</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCustomers.map((row) => (
                      <tr key={row.customerId ?? 'walk-in'}>
                        <Td>
                          <span className="font-medium">{row.name}</span>
                          {row.code && <p className="text-xs text-muted">{row.code}</p>}
                        </Td>
                        <Td numeric className="text-secondary">{row.saleCount}</Td>
                        <Td numeric className="font-medium">{formatMoney(row.revenue, format)}</Td>
                        <Td
                          numeric
                          className={
                            Number(row.outstanding) > 0 ? 'text-red-600 dark:text-red-400' : 'text-secondary'
                          }
                        >
                          {formatMoney(row.outstanding, format)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>

          <Can permission={P.REPORT_FINANCIAL_VIEW}>
            <Card className="mt-4">
              <CardHeader
                title="What it cost to run"
                description="Expenses the business accepted in this period — approved, whether or not they are paid yet."
              />
              {data.expensesByCategory.length === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title="No expenses in this period"
                  description="Only approved and paid expenses count towards profit."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Category</Th>
                      <Th numeric>Count</Th>
                      <Th numeric>Amount</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expensesByCategory.map((row) => (
                      <tr key={row.categoryId}>
                        <Td className="font-medium">{row.name}</Td>
                        <Td numeric className="text-secondary">{row.expenseCount}</Td>
                        <Td numeric className="font-medium">{formatMoney(row.amount, format)}</Td>
                      </tr>
                    ))}
                    <tr className="border-t border-subtle">
                      <Td className="font-medium">Total</Td>
                      <Td numeric />
                      <Td numeric className="font-medium">
                        {formatMoney(summary.expenses ?? '0', format)}
                      </Td>
                    </tr>
                    {Number(summary.expensesUnpaid ?? '0') > 0 && (
                      <tr>
                        <Td className="text-muted">Of which still to pay</Td>
                        <Td numeric />
                        <Td numeric className="text-red-600 dark:text-red-400">
                          {formatMoney(summary.expensesUnpaid ?? '0', format)}
                        </Td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card>
          </Can>
        </div>
      ) : null}
    </>
  );
}

/** One headline figure. A number this size does not need a chart around it. */
function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-sm text-muted">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular">{value}</p>
        {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
      </CardBody>
    </Card>
  );
}
